import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';

const faqs = [
  {
    id: 1,
    question: 'Hangi dosya formatlarında teslim yapılır?',
    answer: 'Tüm tasarımlar PNG, JPG ve PDF formatında teslim edilir. İhtiyaç durumunda AI, PSD gibi kaynak dosyalar da sağlanabilir.'
  },
  {
    id: 2,
    question: 'Revize süreci nasıl işler?',
    answer: 'Her pakette belirtilen revize hakkınız vardır. Revize talepleri tasarımın ilk teslimatından sonraki 48 saat içinde iletilmelidir. Revizyeler genellikle 24-48 saat içinde tamamlanır.'
  },
  {
    id: 3,
    question: 'Teslimat süresi ne kadar?',
    answer: 'Mini paket 3-5 iş günü, aylık paketler aybaşında toplu teslim edilir. Kurumsal kimlik projesi 7-10 iş günü, Sürekli Destek paketi ise aylık periyotlarla çalışır.'
  },
  {
    id: 4,
    question: 'Ödeme nasıl yapılır?',
    answer: 'Havale/EFT, kredi kartı veya PayTR üzerinden ödeme yapabilirsiniz. Aylık paketlerde peşin ödeme zorunludur. Büyük projelerde %50 ön ödeme alınır.'
  },
  {
    id: 5,
    question: 'Sadece tasarım mı yapılıyor, içerik de üretiliyor mu?',
    answer: 'Evet, görsel tasarımların yanı sıra temel metin içerik önerileri de sunuyoruz. Ancak detaylı copywriting için ayrı bir hizmet paketi gerekebilir.'
  },
  {
    id: 6,
    question: 'İptal ve iade politikası nedir?',
    answer: 'Çalışmaya başlandıktan sonra iade yapılmaz. Ancak memnun kalmazsanız, revize haklarınızı kullanarak istediğiniz değişiklikleri yaptırabilirsiniz.'
  },
  {
    id: 7,
    question: 'Hangi sektörlere hizmet veriyorsunuz?',
    answer: 'Restoran, kafe, e-ticaret, spor salonları, güzellik salonları, danışmanlık firmaları ve daha birçok sektöre profesyonel tasarım hizmetleri sunuyoruz.'
  },
  {
    id: 8,
    question: 'Sosyal medya yönetimi de yapıyor musunuz?',
    answer: 'Hayır, sadece tasarım hizmeti veriyoruz. Ancak tasarımlar sosyal medya boyutlarına uygun olarak hazırlanır ve paylaşıma hazır şekilde teslim edilir.'
  }
];

const FAQSection = () => {
  const [openId, setOpenId] = useState(null);

  const toggleFAQ = (id) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <section id="sss" className="py-24 bg-dark-grey relative overflow-hidden">
      {/* Animated Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gold/5 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.div 
            initial={{ rotate: -180, scale: 0 }}
            whileInView={{ rotate: 0, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-flex items-center gap-2 mb-4 px-4 py-2 bg-gold/10 border border-gold/20 rounded-full"
          >
            <HelpCircle className="w-4 h-4 text-gold" />
            <span className="text-gold text-sm font-mono uppercase tracking-wider">S.S.S</span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-4xl md:text-6xl font-bold text-white mb-6"
          >
            Sıkça Sorulan <span className="text-gold">Sorular</span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Merak ettiklerinizin cevaplarını burada bulabilirsiniz.
          </motion.p>
        </motion.div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={faq.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="relative"
            >
              <motion.button
                onClick={() => toggleFAQ(faq.id)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="w-full bg-black/40 backdrop-blur-sm border border-white/10 rounded-2xl p-6 text-left hover:border-gold/30 transition-all duration-300"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="text-white font-semibold text-lg pr-4">
                    {faq.question}
                  </span>
                  <motion.div
                    animate={{ rotate: openId === faq.id ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown className="w-6 h-6 text-gold" />
                  </motion.div>
                </div>

                <AnimatePresence>
                  {openId === faq.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ 
                        height: 'auto', 
                        opacity: 1,
                        transition: {
                          height: { duration: 0.4 },
                          opacity: { duration: 0.3, delay: 0.1 }
                        }
                      }}
                      exit={{ 
                        height: 0, 
                        opacity: 0,
                        transition: {
                          height: { duration: 0.3 },
                          opacity: { duration: 0.2 }
                        }
                      }}
                      className="overflow-hidden"
                    >
                      <motion.div 
                        initial={{ y: -10 }}
                        animate={{ y: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className="pt-4 text-gray-400 leading-relaxed border-t border-white/5 mt-4"
                      >
                        {faq.answer}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-gray-400 mb-6 text-lg">
            Başka sorunuz mu var?
          </p>
          <motion.a
            href="#teklif-formu"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center gap-3 px-8 py-4 bg-gold text-black font-semibold rounded-full hover:bg-gold-light transition-all duration-300 shadow-lg hover:shadow-gold/50"
          >
            Bizimle İletişime Geçin
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;